import type { NetworkRequestData } from "@imagine/sandbox-instrumentation";
import { lens } from "@dhmk/zustand-lens";

const MAX_ENTRIES = 200;

export type NetworkLogEntry = {
  id: string;
  request: Partial<NetworkRequestData>;
};

type NetworkLogsState = {
  logs: Array<NetworkLogEntry>;
};

type NetworkLogsActions = {
  append: (entry: NetworkLogEntry) => void;
  appendMany: (entries: Array<NetworkLogEntry>) => void;
  reset: () => void;
};

export type NetworkLogsSlice = NetworkLogsState & NetworkLogsActions;

const sortAndLimitLogs = (
  logs: Array<NetworkLogEntry>,
): Array<NetworkLogEntry> => {
  return [...logs]
    .sort((a, b) => {
      const tA = Number(new Date(a.request.timestamp ?? 0));
      const tB = Number(new Date(b.request.timestamp ?? 0));
      const validTA = Number.isFinite(tA) ? tA : 0;
      const validTB = Number.isFinite(tB) ? tB : 0;
      return validTA - validTB;
    })
    .slice(-MAX_ENTRIES);
};

const mergeLogs = (
  existing: Array<NetworkLogEntry>,
  additions: Array<NetworkLogEntry>,
): Array<NetworkLogEntry> => {
  if (!additions.length) return existing;
  const entriesById = new Map<string, NetworkLogEntry>();
  for (const entry of [...existing, ...additions]) {
    entriesById.set(entry.id, entry);
  }
  return sortAndLimitLogs(Array.from(entriesById.values()));
};

export const networkLogsSlice = lens<NetworkLogsSlice>((set, get, api) => ({
  logs: [],
  append: (entry) =>
    set((state) => ({
      logs: mergeLogs(state.logs, [entry]),
    })),
  appendMany: (entries) =>
    set((state) => ({
      logs: mergeLogs(state.logs, entries),
    })),
  reset: () => set({ logs: [] }),
}));
